import { useEffect, useRef } from "react";

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"]):not([disabled])';

export default function useDialogA11y(containerRef, onClose, isActive) {
  const active = isActive !== undefined ? isActive : true;
  const previousFocusRef = useRef(null);
  const onCloseRef = useRef(onClose);
  useEffect(() => { onCloseRef.current = onClose; });

  useEffect(() => {
    if (!active) return;

    const container = containerRef.current;
    if (!container) return;

    previousFocusRef.current = document.activeElement;

    requestAnimationFrame(() => {
      if (!containerRef.current) return;
      const focusable = containerRef.current.querySelectorAll(FOCUSABLE);
      if (focusable.length > 0) {
        focusable[0].focus();
      } else {
        containerRef.current.setAttribute("tabindex", "-1");
        containerRef.current.focus();
      }
    });

    const hiddenElements = [];
    let node = container;
    while (node && node !== document.body) {
      const parent = node.parentElement;
      if (!parent) break;
      Array.from(parent.children).forEach((sibling) => {
        if (
          sibling !== node &&
          sibling.getAttribute("aria-hidden") !== "true"
        ) {
          sibling.setAttribute("aria-hidden", "true");
          sibling.dataset.dialogA11yHidden = "true";
          hiddenElements.push(sibling);
        }
      });
      node = parent;
    }

    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        if (onCloseRef.current) onCloseRef.current();
        return;
      }

      if (e.key === "Tab") {
        const current = containerRef.current;
        if (!current) return;
        const focusable = Array.from(current.querySelectorAll(FOCUSABLE));
        if (focusable.length === 0) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];

        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      hiddenElements.forEach((el) => {
        if (el.dataset.dialogA11yHidden === "true") {
          el.removeAttribute("aria-hidden");
          delete el.dataset.dialogA11yHidden;
        }
      });
      if (previousFocusRef.current && previousFocusRef.current.focus) {
        previousFocusRef.current.focus();
      }
    };
  }, [containerRef, active]);
}
